import React, { ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCalcContext } from 'hooks/context/calc';
import { useCheckoutCurrency } from 'hooks/context/checkoutSession';
import { Input } from 'components/Input';
import { Icon } from 'components/Icon';
import { fromCents, fromCentsWithLocale } from 'utils/Currency';

import { MAX_VARIABLE_AMOUNT, MIN_VARIABLE_AMOUNT, TOTALS_TEST_IDS } from '../const';

import styles from '../styles.module.scss';

export const VariableTotal = () => {
  const { t } = useTranslation();
  const currency = useCheckoutCurrency();
  const { setPayableTotalAmount, minAmount, setPendingAmount, convenienceFee, tipAmount } = useCalcContext();
  const [displayTotal, setDisplayTotal] = useState<string>(t('money', { amount: fromCentsWithLocale(minAmount || 0, currency) }));
  const [isInputError, setInputError] = useState(false);
  const maxVariableAmtError = t('variableInputError', {
    minVariableAmount: fromCents(minAmount || MIN_VARIABLE_AMOUNT),
    maxVariableAmount: fromCents(MAX_VARIABLE_AMOUNT),
  });

  const handleAmountChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const numValue = Number(e.target.value.replace(/\D/gi, ''));
      setPayableTotalAmount && setPayableTotalAmount(numValue);
      setPendingAmount && setPendingAmount(numValue + convenienceFee + tipAmount);

      setDisplayTotal(t('money', { amount: fromCentsWithLocale(numValue, currency) }));
      const isValueNotInRange = numValue < (minAmount || MIN_VARIABLE_AMOUNT) || numValue > MAX_VARIABLE_AMOUNT;
      setInputError(isValueNotInRange);
    },
    [setPayableTotalAmount, setDisplayTotal, t, currency, minAmount],
  );

  return (
    <>
      <div className={styles.variableAmt} data-testid={TOTALS_TEST_IDS.variableAmount}>
        <span>{t('variableAmount')}</span>
        <Input
          label={t('amount')}
          name="variableAmount"
          type="tel"
          value={displayTotal}
          hasError={isInputError}
          onChange={handleAmountChange}
        />
      </div>
      {isInputError && (
        <div className={styles.variableAmtError}>
          <p>
            <Icon className={styles.icon} name="error" />
            {maxVariableAmtError}
          </p>
        </div>
      )}
    </>
  );
};
